import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'

import { styled } from 'styled-system/jsx'

const Backdrop = styled(motion.div, {
  base: {
    display: { base: 'flex', lg: 'none' },
    position: 'fixed',
    inset: '0',
    backgroundColor: 'rgba(26, 19, 48, 0.45)',
    zIndex: '90',
    justifyContent: 'flex-end',
  },
})

const Panel = styled(motion.aside, {
  base: {
    width: '100%',
    maxWidth: '320px',
    height: '100%',
    backgroundColor: '#FFFFFF',
    boxShadow: '-24px 0 60px -20px rgba(26, 19, 48, 0.28)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
})

const Header = styled('header', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingX: '20px',
    paddingY: '18px',
    borderBottom: '1px solid',
    borderColor: '#F0EDFA',
  },
})

const Identity = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    minWidth: '0',
  },
})

const IdentityText = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: '0',
  },
})

const IdentityName = styled('span', {
  base: {
    fontFamily: 'display',
    fontSize: '17px',
    fontWeight: '700',
    color: 'quickpass.ink',
    letterSpacing: '-0.2px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
})

const IdentityLabel = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '600',
    letterSpacing: '0.4px',
    textTransform: 'uppercase',
    color: 'quickpass.inkSoft',
  },
})

const CloseButton = styled('button', {
  base: {
    width: '36px',
    height: '36px',
    borderRadius: 'pill',
    border: 'none',
    backgroundColor: 'quickpass.surfaceCool',
    color: 'quickpass.inkSoft',
    fontSize: '18px',
    lineHeight: '1',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: '0',
    transition:
      'background-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}',
    _hover: {
      backgroundColor: 'quickpass.warmth',
      color: '#FFFFFF',
    },
  },
})

const NavScroll = styled('nav', {
  base: {
    flex: '1',
    overflowY: 'auto',
    paddingX: '12px',
    paddingY: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
})

const NavRow = styled(Link, {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    minHeight: '52px',
    paddingX: '16px',
    paddingY: '12px',
    borderRadius: 'md',
    fontFamily: 'body',
    fontSize: '16px',
    fontWeight: '500',
    color: 'quickpass.ink',
    textDecoration: 'none',
    cursor: 'pointer',
    transition:
      'background-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}',
    _hover: {
      backgroundColor: 'quickpass.surfaceCool',
    },
  },
  variants: {
    isActive: {
      true: {
        backgroundColor: 'quickpass.surfaceCool',
        color: 'quickpass.primary',
        fontWeight: '700',
      },
    },
  },
})

const NavRowBadge = styled('span', {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '20px',
    height: '20px',
    paddingX: '6px',
    borderRadius: 'pill',
    backgroundColor: 'quickpass.primary',
    color: '#FFFFFF',
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    lineHeight: '1',
    flexShrink: '0',
  },
})

const Footer = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    paddingX: '16px',
    paddingY: '16px',
    borderTop: '1px solid',
    borderColor: '#F0EDFA',
  },
})

const ModeSwitchRow = styled('button', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    minHeight: '48px',
    width: '100%',
    borderRadius: 'pill',
    border: 'none',
    backgroundColor: 'quickpass.surfaceCool',
    color: 'quickpass.primary',
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'opacity {durations.fast} {easings.appleStandard}',
    _hover: {
      opacity: '0.85',
    },
  },
})

const SignOutRow = styled('button', {
  base: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '48px',
    width: '100%',
    borderRadius: 'pill',
    border: '1.5px solid',
    borderColor: '#E5E0F5',
    backgroundColor: 'transparent',
    color: 'quickpass.inkSoft',
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    transition:
      'border-color {durations.fast} {easings.appleStandard}, color {durations.fast} {easings.appleStandard}',
    _hover: {
      borderColor: 'quickpass.warmth',
      color: 'quickpass.warmth',
    },
  },
})

export {
  Backdrop,
  Panel,
  Header,
  Identity,
  IdentityText,
  IdentityName,
  IdentityLabel,
  CloseButton,
  NavScroll,
  NavRow,
  NavRowBadge,
  Footer,
  ModeSwitchRow,
  SignOutRow,
}
